import React, { useEffect, useMemo, useState } from "react";

import {
  Stack,
  DisplayText,
  TextStyle,
  Button,
  Select,
} from "@shopify/polaris";
import useTranslation from "next-translate/useTranslation";
import cn from "classnames";
import dayjs from "dayjs";
import filter from "lodash/filter";
import get from "lodash/get";

import Title from "../../components/Title";
import DiscordFillIcon from "../../icons/DiscordFill";
import { DISCORD_TELEGRAM_NAMESPACE } from "../../../../../../utils/i18nNameSpaces";

import styles from "./styles.module.scss";
import {
  DISCORD_EMAIL_BOT_LINK,
  DISCORD_TG_BOT_LINK,
} from "../../../../../../utils/constants";
import { useRouter } from "next/router";
import { getExchangeAmount } from "../../../../../../helpers/getExchangeAmount";

const Index = ({ guilds = [], user, authorized, namespace, botPrice }) => {
  const [amount, setAmount] = useState(botPrice);
  const [selectedGuild, setSelectedGuild] = useState(null);
  const { locale } = useRouter();

  const { t } = useTranslation(namespace);

  useEffect(() => {
    (async () => {
      const amount =
        locale === "uk" ? await getExchangeAmount(botPrice) : botPrice;
      setAmount(amount);
    })();
  }, [locale, botPrice]);

  const now = dayjs();

  // array of guilds with expired subscription
  const subExpired = filter(
    guilds,
    (el) => el?.status === 0 && now.isAfter(el?.trial_end)
  );

  // array of guilds with expired trial
  const trialExpired = filter(
    guilds,
    (el) => el?.status === null && now.isAfter(el?.trial_end)
  );

  // array of guilds with active subscription
  const subActive = filter(
    guilds,
    (el) => el?.status === 1 && now.isAfter(el?.trial_end)
  );

  // array of guilds with active trial
  const trialActive = filter(guilds, (el) => now.isBefore(el?.trial_end));

  const subscribed = subActive.length > 0 ? subActive : trialActive || [];
  const unsubscribed = subExpired.length > 0 ? subExpired : trialExpired || [];

  const hasUnsubscribed = subExpired.length > 0 || trialExpired.length > 0;
  const allActive = guilds.length > 0 && !hasUnsubscribed;

  const url = useMemo(() => {
    if (!authorized || guilds.length <= 1) {
      return namespace === DISCORD_TELEGRAM_NAMESPACE
        ? DISCORD_TG_BOT_LINK
        : DISCORD_EMAIL_BOT_LINK;
    }

    const guildId = selectedGuild || get(unsubscribed, "0.guild_id");

    return namespace === DISCORD_TELEGRAM_NAMESPACE
      ? `/discord-telegram/checkout?guildId=${guildId}&userId=${user.id}`
      : `/discord-email/checkout?guildId=${guildId}&userId=${user.id}`;
  }, [selectedGuild, authorized, guilds, user]);

  const isSubExpired = useMemo(
    () =>
      guilds.some((item) => {
        if (!selectedGuild) {
          return (
            item.guild_id === get(unsubscribed, "0.guild_id") &&
            item.status === 0
          );
        }
        return item.guild_id === selectedGuild && item.status === 0;
      }),
    [selectedGuild, guilds]
  );
  return (
    <section className={styles["Pricing"]}>
      <Title>{t("pricing.title")}</Title>
      <div
        className={cn(styles["card"], { [styles["error"]]: hasUnsubscribed })}
      >
        <Stack vertical spacing="extraLoose" alignment="center">
          <DisplayText size="large">
            <span className={styles["card-title"]}>
              {t("pricing.card.title")}
            </span>
          </DisplayText>

          <DisplayText size="large">
            <span
              style={{ fontWeight: 400, textAlign: "center", display: "block" }}
            >
              {t("pricing.card.subtitle")}
            </span>
          </DisplayText>

          <Stack vertical spacing="loose" alignment="center">
            <DisplayText size="large">
              <span style={{ fontWeight: 600 }}>
                <TextStyle variation="positive">
                  {t("pricing.card.price", {
                    amount,
                  })}
                </TextStyle>
              </span>
            </DisplayText>
            {allActive ? (
              <div style={{ fontWeight: 600, fontSize: 16, lineHeight: 1.5 }}>
                <TextStyle variation="positive">
                  {subActive.length > 0 ? (
                    <>
                      {`${t("pricing.subscription.active")}`}{" "}
                      <TextStyle variation="strong">{`${subscribed
                        .map((el) => el.name)
                        .join(", ")}`}</TextStyle>
                    </>
                  ) : (
                    <>
                      <Stack vertical spacing="tight">
                        <TextStyle>{`${t("pricing.trial.active")}`}</TextStyle>
                        <Stack vertical spacing="extraTight">
                          {subscribed.map((el) => (
                            <TextStyle variation="strong">
                              {`${el.name} at ${dayjs(el.trial_end).format(
                                "MMM D, YYYY HH:mm"
                              )}`}
                            </TextStyle>
                          ))}
                        </Stack>
                      </Stack>
                    </>
                  )}
                </TextStyle>
              </div>
            ) : hasUnsubscribed ? (
              <div style={{ fontWeight: 600, fontSize: 16, lineHeight: 1.5 }}>
                <TextStyle variation="negative">
                  {`${t(
                    subExpired.length > 0
                      ? "pricing.subscription.expired"
                      : "pricing.trial.expired"
                  )}`}{" "}
                  <TextStyle variation="strong">
                    {`${unsubscribed.map((el) => el.name).join(", ")}`}
                  </TextStyle>
                </TextStyle>
              </div>
            ) : (
              <div style={{ fontWeight: 600, fontSize: 16, lineHeight: 1.5 }}>
                <TextStyle variation="subdued">
                  {t("pricing.card.trial")}
                </TextStyle>
              </div>
            )}
          </Stack>
          {/* {allActive ? null : (
            <Stack alignment="trailing" spacing="extraTight">
              {hasUnsubscribed && (
                <Select
                  label={t("labels.guild")}
                  options={unsubscribed
                    .map((e) => ({
                      value: e.guild_id,
                      label: e.name,
                    }))
                    .concat(
                      trialExpired.map((e) => ({
                        value: e.guild_id,
                        label: e.name,
                      }))
                    )}
                  value={selectedGuild}
                  onChange={setSelectedGuild}
                  labelInline
                />
              )}
              <Button primary icon={DiscordFillIcon} size="large" url={url}>
                {t(
                  hasUnsubscribed
                    ? subExpired.length > 0 && isSubExpired
                      ? "actions.renew"
                      : "actions.subscribe"
                    : "actions.try"
                )}
              </Button>
            </Stack>
          )} */}
        </Stack>
      </div>
    </section>
  );
};

export default Index;
